import React, { forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { LoaderCircle } from "lucide-react";

const googleAuthSchema = z.object({
    GOOGLE_CLIENT_ID: z.string().min(1, "Google Client ID is required"),
    GOOGLE_CLIENT_SECRET: z.string().min(1, "Google Client Secret is required"),
});

type GoogleAuthData = z.infer<typeof googleAuthSchema>;

type Props = {
    data?: GoogleAuthData;
    initialData?: any;
    onDataChange?: (data: any) => void;
    readOnly?: boolean;
    error?: string;
    submitButton?: React.ReactNode;
    onSubmit?: (data: any) => void;
    hideSubmitButton?: boolean;
    loading?: boolean;
};

const GoogleAuthForm = forwardRef<any, Props>(
    (
        {
            data,
            initialData = {},
            onDataChange,
            readOnly = false,
            error,
            submitButton,
            onSubmit,
            hideSubmitButton = false,
            loading = false,
        },
        ref
    ) => {
        const form = useForm<GoogleAuthData>({
            resolver: zodResolver(googleAuthSchema),
            defaultValues: {
                GOOGLE_CLIENT_ID: initialData?.GOOGLE_CLIENT_ID || data?.GOOGLE_CLIENT_ID || "",
                GOOGLE_CLIENT_SECRET: initialData?.GOOGLE_CLIENT_SECRET || data?.GOOGLE_CLIENT_SECRET || "",
            },
        });

        // Expose methods to parent component
        useImperativeHandle(ref, () => ({
            validateForm: async () => {
                const result = await form.trigger();
                if (!result) {
                    // Get the first error message
                    const errors = form.formState.errors;
                    const firstError = Object.values(errors)[0];
                    if (firstError?.message) {
                        console.error("Google Auth validation error:", firstError.message);
                    }
                }
                return result;
            },
            getFormData: () => {
                return form.getValues();
            },
            submitForm: () => {
                if (onSubmit) {
                    form.handleSubmit(onSubmit)();
                }
            },
        }));

        // Watch for changes and notify parent
        React.useEffect(() => {
            const subscription = form.watch((value) => {
                if (onDataChange) {
                    onDataChange(value);
                }
            });
            return () => subscription.unsubscribe();
        }, [form, onDataChange]);

        const handleSubmit = (formData: GoogleAuthData) => {
            if (onSubmit) {
                onSubmit(formData);
            }
        };

        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <div className="flex items-center gap-2">
                                <ExclamationTriangleIcon className="h-4 w-4" />
                                <AlertTitle className="mb-0">{error}</AlertTitle>
                            </div>
                        </Alert>
                    )}

                    <div className="space-y-4">
                        <FormField
                            control={form.control}
                            name="GOOGLE_CLIENT_ID"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel htmlFor={field.name} className="text-sm font-medium">
                                        Google Client ID
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            readOnly={readOnly}
                                            placeholder="Enter your Google OAuth Client ID"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="GOOGLE_CLIENT_SECRET"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel htmlFor={field.name} className="text-sm font-medium">
                                        Google Client Secret
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            type="password"
                                            readOnly={readOnly}
                                            placeholder="Enter your Google OAuth Client Secret"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className="rounded-lg border p-4">
                        <p className="text-sm">
                            <strong>Setup Instructions:</strong>
                        </p>
                        <ol className="mt-2 list-inside list-decimal space-y-1 text-sm">
                            <li>Go to the Google Cloud Console</li>
                            <li>Create a new project or select an existing one</li>
                            <li>Enable the Google+ API and Google OAuth2 API</li>
                            <li>Go to Credentials and create OAuth 2.0 Client IDs</li>
                            <li>Configure authorized redirect URIs for your application</li>
                            <li>Copy the Client ID and Client Secret</li>
                        </ol>
                    </div>

                    {!hideSubmitButton && (
                        <div className="flex justify-end">
                            {submitButton || (
                                <Button type="submit" disabled={readOnly || loading}>
                                    {loading && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
                                    Save Configuration
                                </Button>
                            )}
                        </div>
                    )}
                </form>
            </Form>
        );
    }
);

GoogleAuthForm.displayName = "GoogleAuthForm";

export default GoogleAuthForm;
